import { useState } from 'react'
import { T } from '@/lib/theme'
import { CHAIN_ID, chainMeta } from '@/lib/contracts'

// The app is SINGLE-CHAIN PER DEPLOYMENT - it reads the chain its generated address book
// (contracts.js) was built for, selected at build time by VITE_TARGET_CHAIN (see wagmi-config.js).
// So this control REFLECTS the one active network; it does NOT switch the wallet underneath a fixed
// address book, which would reintroduce the "wallet on Sepolia, app still reading mainnet" split this
// codebase warns against.
//
// It shows ONLY the active chain: the mainnet build (VITE_TARGET_CHAIN=1) shows Ethereum and nothing
// else - no Sepolia/testnet entry leaks onto the public site - and the Sepolia build (=11155111) shows
// Sepolia. Names/badges come from the ONE canonical `CHAIN_META` table, not a second hand-kept list.
//
// Cross-network navigation (jump to another deployment's URL) is a real future feature: push those
// other deployments as `{ id, name, url }` into NETWORKS and the dropdown below returns automatically
// for length > 1. Until then it renders as a clean, non-interactive badge.
const NETWORKS = [{ id: CHAIN_ID, ...chainMeta(CHAIN_ID) }]
const ACTIVE = NETWORKS[0]

export function ChainSelect() {
  const [open, setOpen] = useState(false)
  const multi = NETWORKS.length > 1
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => multi && setOpen((o) => !o)}
        style={{ ...chainSelectBtn, cursor: multi ? 'pointer' : 'default' }}
        aria-haspopup={multi ? 'listbox' : undefined}
      >
        <EthGlyph />
        <span style={{ color: T.text, fontFamily: T.fontM, fontWeight: 700, fontSize: T.fs.body }}>{ACTIVE.name}</span>
        {ACTIVE.testnet && <span style={testnetTag}>Testnet</span>}
        {multi && <span style={{ color: T.textDim, fontSize: 10 }}>▾</span>}
      </button>
      {multi && open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 15 }} onClick={() => setOpen(false)} />
          <div style={chainSelectMenu}>
            {NETWORKS.map((n) => (
              <div key={n.id} style={{ ...chainSelectMenuItem, cursor: 'default' }}>
                <EthGlyph />
                <span style={{ color: T.text, fontFamily: T.fontM, fontWeight: 700, fontSize: T.fs.body }}>{n.name}</span>
                {n.testnet && <span style={testnetTag}>Testnet</span>}
                {n.id === ACTIVE.id && (
                  <span style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: T.positive }} />
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function EthGlyph({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M12 1L12 9.6L19 12.7L12 1Z" fill={T.accent} opacity="0.9" />
      <path d="M12 1L5 12.7L12 9.6L12 1Z" fill={T.accent} opacity="0.55" />
      <path d="M12 16.9L12 23L19 14L12 16.9Z" fill={T.accent} opacity="0.9" />
      <path d="M12 23L12 16.9L5 14L12 23Z" fill={T.accent} opacity="0.55" />
      <path d="M12 15.2L19 12.7L12 9.6L5 12.7L12 15.2Z" fill={T.accent} />
    </svg>
  )
}

const chainSelectBtn = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: T.r.pill,
  border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)', whiteSpace: 'nowrap',
}
const chainSelectMenu = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 20, minWidth: 210,
  background: 'rgba(10,14,26,0.98)', backdropFilter: T.glassBlur, WebkitBackdropFilter: T.glassBlur,
  border: `1px solid ${T.border}`, borderRadius: T.r.md, boxShadow: T.shadowMed, overflow: 'hidden',
}
const chainSelectMenuItem = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px',
}
const testnetTag = {
  fontSize: 9, fontFamily: T.fontM, letterSpacing: 0.5, textTransform: 'uppercase', color: T.textDim,
  border: `1px solid ${T.border}`, borderRadius: 4, padding: '1px 5px',
}
